import { t } from "@lingui/macro";
import { TableSortLabel } from "@mui/material";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import get from "lodash/get";
import moment from "moment";
import React, { ReactElement, useEffect, useState } from "react";

import {
	ClientSortField,
	GmClientFragment,
	GoalsMapperClientFilterInput,
	GoalsmapperClientsQueryVariables,
	GoalsmapperSortInput,
	OrderDirection,
	useGoalsmapperClientsQuery,
} from "@/saleor/api";

export interface ClientTableProps {
	onSelectedChange?: (ids: string[], clients: GmClientFragment[]) => void;
	selectedIds?: string[];
	productQuantityAvailable?: number;
	idIdentity?: "id" | "person.id";
	emptyMessage?: string;
	filter: GoalsMapperClientFilterInput;
	selectedClients: GmClientFragment[];
}

interface Column {
	id: "name" | "person.dateOfBirth" | "tags" | "person.mobileContact";
	label: string;
	subtitle?: string;
	minWidth?: number;
	align?: "right" | "left";
	sortField?: ClientSortField;
	renderer?: (item: GmClientFragment) => ReactElement | string | null;
}

const columns: readonly Column[] = [
	{
		id: "name",
		label: t`Name`,
		subtitle: t`Email Address`,
		minWidth: 170,
		sortField: ClientSortField.Client,
		renderer: (item: GmClientFragment) => {
			return (
				<Stack>
					<div>{item.person.name}</div>
					<div className="text-xs text-gray-800">{item.person.email}</div>
				</Stack>
			);
		},
	},
	{
		id: "person.dateOfBirth",
		label: t`DOB`,
		minWidth: 100,
		sortField: ClientSortField.Dob,
		renderer: (item: GmClientFragment) => {
			if (item.person.dateOfBirth) {
				return moment(item.person.dateOfBirth).format("LL");
			}
			return "-";
		},
	},
	{
		id: "person.mobileContact",
		label: t`Number`,
		minWidth: 170,
		align: "left",
	},
	{
		id: "tags",
		label: t`Tags`,
		minWidth: 170,
		align: "left",
		renderer: (item: GmClientFragment) => {
			if (!item.tags) {
				return null;
			}
			return (
				<Stack direction="row">
					{item.tags
						.filter((t) => !!t)
						.slice(0, 1)
						.map((t) => (
							<Chip
								key={t?.id || ""}
								color="primary"
								variant="outlined"
								label={t?.title || ""}
								className="text-gray-900"
							/>
						))}
				</Stack>
			);
		},
	},
];

const GMClientTable = ({
	onSelectedChange,
	selectedIds: initialSelectedIds,
	idIdentity = "id",
	emptyMessage,
	selectedClients,
	filter,
}: ClientTableProps) => {
	const [page, setPage] = React.useState(0);
	const [selected, setSelected] = React.useState<string[]>(initialSelectedIds || []);
	const [rowsPerPage, setRowsPerPage] = useState<number>(10);
	const [sortInput, setSortInput] = useState<GoalsmapperSortInput>({
		field: ClientSortField.Client,
		direction: OrderDirection.Asc,
	});
	const [contactQuery, setContactQuery] = useState<GoalsmapperClientsQueryVariables>({
		filter,
		first: rowsPerPage,
		sortBy: sortInput,
	});
	const [currentPage, setCurrentPage] = useState(0);

	const { loading, data, fetchMore } = useGoalsmapperClientsQuery({
		variables: contactQuery,
		fetchPolicy: "network-only",
	});

	useEffect(() => {
		setContactQuery((query) => ({
			...query,
			first: rowsPerPage,
			filter: filter,
			sortBy: sortInput,
		}));
		setCurrentPage(0);
	}, [filter, sortInput]);

	const clients: GmClientFragment[] = data?.clients?.edges?.map((edge) => edge.node) || [];
	const totalCount: number = data?.clients?.totalCount || 0;
	const isEmpty: boolean = totalCount === 0 && !loading;

	useEffect(() => {
		if (initialSelectedIds && initialSelectedIds.length > 0) {
			setSelected([...initialSelectedIds]);
		} else {
			setSelected([]);
		}
	}, [initialSelectedIds]);

	const handleChangePage = (event: unknown, newPage: number) => {
		if (newPage > currentPage) {
			fetchMore({
				variables: {
					after: data?.clients?.pageInfo?.endCursor,
				},
			});
		}
		setCurrentPage(newPage);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setContactQuery((query) => ({
			...query,
			first: +event.target.value,
		}));
		setRowsPerPage(+event.target.value);
		setCurrentPage(0);
	};

	const isSelected = (client: GmClientFragment) => {
		if (idIdentity === "id") return selected.indexOf(client.id) !== -1;
		else return selected.indexOf(client.person.id) !== -1;
	};

	const getChangeId = (client: GmClientFragment) => {
		if (idIdentity === "id") return client.id;
		else return client.person.id;
	};

	const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.checked) {
			const newSelecteds = clients.map((n) => getChangeId(n));
			setSelected(newSelecteds);
			onSelectedChange && onSelectedChange(newSelecteds, clients);
			return;
		}
		setSelected([]);
		onSelectedChange && onSelectedChange([], []);
	};

	const handleClick = (event: React.MouseEvent<unknown>, client: GmClientFragment) => {
		const changeId = getChangeId(client);
		const selectedIndex = selected.indexOf(changeId);
		//console.log(selectedIndex);
		let newSelected: string[] = [];
		let newSelectedClients: GmClientFragment[] = [];

		if (selectedIndex === -1) {
			newSelected = newSelected.concat(selected, changeId);
			newSelectedClients = newSelectedClients.concat(selectedClients, client);
		} else if (selectedIndex === 0) {
			newSelected = newSelected.concat(selected.slice(1));
			newSelectedClients = newSelectedClients.concat(selectedClients.slice(1));
		} else if (selectedIndex === selected.length - 1) {
			newSelected = newSelected.concat(selected.slice(0, -1));
			newSelectedClients = newSelectedClients.concat(selectedClients.slice(0, -1));
		} else if (selectedIndex > 0) {
			newSelected = newSelected.concat(
				selected.slice(0, selectedIndex),
				selected.slice(selectedIndex + 1)
			);
			newSelectedClients = newSelectedClients.concat(
				selectedClients.slice(0, selectedIndex),
				selectedClients.slice(selectedIndex + 1)
			);
		}

		setSelected(newSelected);
		// const selectedClients = clients.filter((c) => newSelected.includes(get(c, idIdentity)));
		onSelectedChange && onSelectedChange(Array.from(newSelected), newSelectedClients);
	};

	// const handleRemoveSelected = () => {};

	const createSortHandler =
		(columnSortField?: ClientSortField) => (event: React.MouseEvent<unknown>) => {
			if (columnSortField) {
				setSortInput((prevState) => {
					let direction: OrderDirection = OrderDirection.Asc;
					if (prevState.field === columnSortField) {
						direction =
							prevState.direction === OrderDirection.Desc
								? OrderDirection.Asc
								: OrderDirection.Desc;
					}
					return {
						field: columnSortField,
						direction: direction,
					};
				});
			}
		};

	const numSelected = selected.length;
	const rowCount = clients?.length;
	const startIndex = currentPage * rowsPerPage;
	const displayClients = clients?.slice(startIndex, startIndex + rowsPerPage);
	return (
		<Box pt={2} minHeight={200}>
			{/* <Box display='flex' flexDirection='row' justifyContent='center' pb={2} pt={1}>
            <Button
                disabled={numSelected === 0}
                startIcon={<CloseIcon />}
                variant={numSelected === 0 ? 'contained' : 'text'}
                color={numSelected === 0 ? 'primary' : 'error'}
                onClick={handleRemoveSelected}
            >
                <Plural
                    id="remove-clients-from-list"
                    value={numSelected}
                    one='Remove client from list'
                    other='Remove clients from list' />
            </Button>
        </Box> */}
			<TableContainer>
				<Table stickyHeader aria-label="sticky table" size="small">
					<TableHead>
						<TableRow>
							<TableCell padding="checkbox">
								<Checkbox
									color="primary"
									indeterminate={numSelected > 0 && numSelected < rowCount}
									checked={rowCount > 0 && numSelected === rowCount}
									onChange={handleSelectAllClick}
									inputProps={{
										"aria-label": "select all recipients",
									}}
								/>
							</TableCell>
							{columns.map((column) => (
								<TableCell
									key={column.id}
									align={column.align}
									style={{ minWidth: column.minWidth }}
								>
									{column.sortField ? (
										<TableSortLabel
											active={sortInput.field === column.sortField}
											direction={
												sortInput.field === column.sortField &&
												sortInput.direction === OrderDirection.Desc
													? "desc"
													: "asc"
											}
											onClick={createSortHandler(column.sortField)}
										>
											<Stack direction="column">
												<Box>{column.label}</Box>
												{column.subtitle && (
													<div className="text-xs font-normal text-gray-600">
														{column.subtitle}
													</div>
												)}
											</Stack>
										</TableSortLabel>
									) : (
										<Stack direction="column">
											<Box>{column.label}</Box>
											{column.subtitle && (
												<div className="text-xs font-normal text-gray-600">
													{column.subtitle}
												</div>
											)}
										</Stack>
									)}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{displayClients
							?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
							?.map((client, index) => {
								const isItemSelected = isSelected(client);
								const labelId = `enhanced-table-checkbox-${index}`;

								return (
									<TableRow
										hover
										role="checkbox"
										tabIndex={-1}
										key={client.id}
										selected={isItemSelected}
										onClick={(event) => handleClick(event, client)}
									>
										<TableCell padding="checkbox">
											<Checkbox
												color="primary"
												checked={isItemSelected}
												inputProps={{
													"aria-labelledby": labelId,
												}}
											/>
										</TableCell>
										{columns.map((column) => {
											const value = get(client, column.id as string);
											return (
												<TableCell key={column.id} align={column.align}>
													{column.renderer
														? column.renderer(client)
														: value}
												</TableCell>
											);
										})}
									</TableRow>
								);
							})}
						{!loading && rowCount === 0 && emptyMessage && (
							<TableRow>
								<TableCell height={400} colSpan={columns.length + 1} align="center">
									{emptyMessage}
								</TableCell>
							</TableRow>
						)}
					</TableBody>
					{loading && (
						<TableFooter>
							<TableRow>
								<TableCell height={400} colSpan={5}>
									<Stack
										direction="row"
										alignItems="center"
										justifyContent={"center"}
										py={2}
									>
										<CircularProgress />
									</Stack>
								</TableCell>
							</TableRow>
						</TableFooter>
					)}
				</Table>
			</TableContainer>
			<TablePagination
				rowsPerPageOptions={[10, 25, 100]}
				component="div"
				count={totalCount}
				rowsPerPage={rowsPerPage}
				page={currentPage}
				onPageChange={handleChangePage}
				onRowsPerPageChange={handleChangeRowsPerPage}
			/>
		</Box>
	);
};

export default GMClientTable;
